import type { WhatsAppMessage } from './whatsappApi';

/**
 * Grouping for the read-only WhatsApp group transcript (client page): a
 * separator per day, and consecutive messages from one sender folded under one
 * header — like the voice transcript (teamChat/VoiceTranscript.tsx). Kept free
 * of UI imports: the mobile app's native transcript uses it too.
 */

// Messages within this window of the previous one by the same sender share a header.
const GROUP_WINDOW_MS = 5 * 60 * 1000;

export interface TranscriptRun {
  key: string;
  senderName: string;
  isFromMe: boolean;
  messages: WhatsAppMessage[];
}

export interface TranscriptDay {
  key: string;
  label: string;
  runs: TranscriptRun[];
}

/** Days → runs of one sender, oldest first. */
export function groupTranscript(messages: WhatsAppMessage[], locale: string): TranscriptDay[] {
  const days: TranscriptDay[] = [];
  for (const m of messages) {
    const at = new Date(m.sentAt);
    const dayKey = at.toDateString();
    let day = days[days.length - 1];
    if (!day || day.key !== dayKey) {
      day = {
        key: dayKey,
        label: at.toLocaleDateString(locale, {
          weekday: 'long',
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        }),
        runs: [],
      };
      days.push(day);
    }
    const run = day.runs[day.runs.length - 1];
    const prev = run?.messages[run.messages.length - 1];
    const sameSender =
      run &&
      run.senderName === m.senderName &&
      run.isFromMe === m.isFromMe &&
      prev &&
      at.getTime() - new Date(prev.sentAt).getTime() <= GROUP_WINDOW_MS;
    if (sameSender) run.messages.push(m);
    else
      day.runs.push({ key: m.id, senderName: m.senderName, isFromMe: m.isFromMe, messages: [m] });
  }
  return days;
}
